// components/workspace-timeline/WorkspaceTimelinePlaybackControls.tsx
import React, { useMemo } from 'react';
import { Repeat, SkipBack, SkipForward } from 'lucide-react';
import {
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Volume2,
  VolumeX,
} from '@/components/ui/lucide-memo';
import { formatTimecode } from './presentation';
import { useKernelStore } from '@/kernel/kernelStores';
import {
  selectWorkspaceIsTimelineLoopEnabled,
  selectWorkspaceIsTimelineMuted,
  selectWorkspaceIsTimelinePlaying,
} from '#lib/workspace-runtime-playback-state';

interface WorkspaceTimelinePlaybackControlsProps {
  laneLeftColumnWidthPx: number;
  totalMs: number;
  workspaceId: string;
  hasAnyTrack: boolean;
  isAtTimelineEnd: boolean;
  isComposedPreparing: boolean;
  primaryControlLabel: string;
  playbackRateLabel: string;
  clampedCursorMs: number;
  onPrimaryAction: () => void;
  onStepBackward: () => void;
  onStepForward: () => void;
  onToggleLoop: () => void;
  onToggleMuted: () => void;
  onOpenPlaybackRateMenu: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const TIMELINE_PLAYBACK_CONTROLS_ROOT_STYLE: React.CSSProperties = { minHeight: '40px' };
const TIMELINE_PLAYBACK_TIMECODE_STYLE: React.CSSProperties = { fontSize: 13, color: '#8E8E8E', paddingLeft: '4px' };
const TIMELINE_PLAYBACK_TIMECODE_CURRENT_STYLE: React.CSSProperties = { fontSize: 'inherit', color: '#ECECEC' };
const TIMELINE_PLAYBACK_RATE_STYLE: React.CSSProperties = { fontSize: 13 };
const TIMELINE_PLAYBACK_ICON_BUTTON_CLASS_NAME = 'h-8 w-8 rounded-full inline-flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
const TIMELINE_PLAYBACK_GHOST_CLASS_NAME = 'text-[#B4B4B4] hover:bg-white/[0.08] hover:text-[#ECECEC]';
const TIMELINE_PLAYBACK_ON_CLASS_NAME = 'bg-white/[0.12] text-[#ECECEC]';
const TIMELINE_PLAYBACK_SMALL_BUTTON_CLASS_NAME = `${TIMELINE_PLAYBACK_ICON_BUTTON_CLASS_NAME} ${TIMELINE_PLAYBACK_GHOST_CLASS_NAME}`;
// The one filled control of the dock, like a selected tool: near-white circle, dark glyph.
const TIMELINE_PLAYBACK_PRIMARY_BUTTON_CLASS_NAME = `${TIMELINE_PLAYBACK_ICON_BUTTON_CLASS_NAME} bg-[#ECECEC] text-[#0D0D0D] hover:bg-white`;
const TIMELINE_PLAYBACK_RATE_BUTTON_BASE_CLASS_NAME = 'h-8 min-w-[44px] px-3 rounded-full inline-flex items-center justify-center font-medium tabular-nums transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

// mm:ss:ff, with the hours only once a timeline reaches them.
const formatDockTimecode = (ms: number): string => {
  const full = formatTimecode(ms);
  return full.startsWith('00:') ? full.slice(3) : full;
};
// Whole seconds, rounded up, so a 4.9 s timeline never reads as 4 s.
const formatDockDuration = (ms: number): string => formatDockTimecode(Math.ceil(ms / 1000) * 1000).replace(/:\d{2}$/, '');

export const WorkspaceTimelinePlaybackControls = React.memo(({
  laneLeftColumnWidthPx,
  totalMs,
  workspaceId,
  hasAnyTrack,
  isAtTimelineEnd,
  isComposedPreparing,
  primaryControlLabel,
  playbackRateLabel,
  clampedCursorMs,
  onPrimaryAction,
  onStepBackward,
  onStepForward,
  onToggleLoop,
  onToggleMuted,
  onOpenPlaybackRateMenu,
}: WorkspaceTimelinePlaybackControlsProps) => {
  // Playback flags come straight from the shared workspace runtime — no props drill.
  const isPlaying = useKernelStore((state) => selectWorkspaceIsTimelinePlaying(state, workspaceId));
  const isLoopEnabled = useKernelStore((state) => selectWorkspaceIsTimelineLoopEnabled(state, workspaceId));
  const isMuted = useKernelStore((state) => selectWorkspaceIsTimelineMuted(state, workspaceId));
  const leftSpacerStyle = useMemo<React.CSSProperties>(() => ({
    width: `${laneLeftColumnWidthPx}px`,
  }), [laneLeftColumnWidthPx]);
  const controlsDisabled = !hasAnyTrack;
  const playbackRateControlLabel = useMemo(
    () => `Select playback speed. Current speed ${playbackRateLabel}`,
    [playbackRateLabel]
  );
  const loopControlLabel = isLoopEnabled ? 'Loop: on' : 'Loop: off';
  const muteControlLabel = isMuted ? 'Sound: muted' : 'Sound: on';
  const timecode = useMemo(() => formatDockTimecode(clampedCursorMs), [clampedCursorMs]);
  const duration = useMemo(() => formatDockDuration(totalMs), [totalMs]);
  const playbackRateButtonClassName = `${TIMELINE_PLAYBACK_RATE_BUTTON_BASE_CLASS_NAME} ${
    playbackRateLabel !== 'x1' ? TIMELINE_PLAYBACK_ON_CLASS_NAME : TIMELINE_PLAYBACK_GHOST_CLASS_NAME
  }`;
  const loopButtonClassName = `${TIMELINE_PLAYBACK_ICON_BUTTON_CLASS_NAME} ${
    isLoopEnabled ? TIMELINE_PLAYBACK_ON_CLASS_NAME : TIMELINE_PLAYBACK_GHOST_CLASS_NAME
  }`;
  const muteButtonClassName = `${TIMELINE_PLAYBACK_ICON_BUTTON_CLASS_NAME} ${
    isMuted ? TIMELINE_PLAYBACK_ON_CLASS_NAME : TIMELINE_PLAYBACK_GHOST_CLASS_NAME
  }`;
  const primaryControlIcon = useMemo(() => {
    if (isComposedPreparing) {
      return <RefreshCw size={16} strokeWidth={2} className="animate-spin" />;
    }
    if (isAtTimelineEnd && !isPlaying) {
      return <RotateCcw size={17} strokeWidth={2.4} />;
    }
    if (isPlaying) {
      return <Pause size={16} strokeWidth={2} fill="currentColor" />;
    }
    return <Play size={16} strokeWidth={2} fill="currentColor" />;
  }, [isAtTimelineEnd, isComposedPreparing, isPlaying]);
  const muteControlIcon = useMemo(() => (
    isMuted ? (
      <VolumeX size={16} strokeWidth={2} fill="currentColor" />
    ) : (
      <Volume2 size={16} strokeWidth={2} fill="currentColor" />
    )
  ), [isMuted]);

  return (
    <div className="mb-1 flex w-full items-center" style={TIMELINE_PLAYBACK_CONTROLS_ROOT_STYLE}>
      <div
        className="flex items-center tabular-nums whitespace-nowrap"
        style={leftSpacerStyle}
        aria-label="Timeline timecode"
      >
        <span style={TIMELINE_PLAYBACK_TIMECODE_STYLE}>
          <span style={TIMELINE_PLAYBACK_TIMECODE_CURRENT_STYLE}>{timecode}</span> / {duration}
        </span>
      </div>
      <div className="flex flex-1 items-center justify-center gap-1">
        <button
          type="button"
          onClick={onStepBackward}
          disabled={controlsDisabled}
          className={TIMELINE_PLAYBACK_SMALL_BUTTON_CLASS_NAME}
          title="Step back 1 frame"
          aria-label="Step back 1 frame"
        >
          <SkipBack size={16} strokeWidth={2} fill="currentColor" />
        </button>
        <button
          type="button"
          onClick={onPrimaryAction}
          disabled={controlsDisabled}
          className={TIMELINE_PLAYBACK_PRIMARY_BUTTON_CLASS_NAME}
          title={primaryControlLabel}
          aria-label={primaryControlLabel}
        >
          {primaryControlIcon}
        </button>
        <button
          type="button"
          onClick={onStepForward}
          disabled={controlsDisabled}
          className={TIMELINE_PLAYBACK_SMALL_BUTTON_CLASS_NAME}
          title="Step forward 1 frame"
          aria-label="Step forward 1 frame"
        >
          <SkipForward size={16} strokeWidth={2} fill="currentColor" />
        </button>
        <button
          type="button"
          onClick={onToggleLoop}
          className={loopButtonClassName}
          title={loopControlLabel}
          aria-label={loopControlLabel}
        >
          <Repeat size={16} strokeWidth={2} />
        </button>
        <button
          type="button"
          onClick={onToggleMuted}
          className={muteButtonClassName}
          title={muteControlLabel}
          aria-label={muteControlLabel}
        >
          {muteControlIcon}
        </button>
      </div>
      {/* As wide as the time column, so the transport sits centred in the dock. */}
      <div className="flex items-center justify-end gap-2" style={leftSpacerStyle}>
        <button
          type="button"
          onClick={onOpenPlaybackRateMenu}
          disabled={controlsDisabled}
          className={playbackRateButtonClassName}
          title={playbackRateControlLabel}
          aria-label={playbackRateControlLabel}
          aria-haspopup="menu"
          style={TIMELINE_PLAYBACK_RATE_STYLE}
        >
          {playbackRateLabel}
        </button>
      </div>
    </div>
  );
});

WorkspaceTimelinePlaybackControls.displayName = 'WorkspaceTimelinePlaybackControls';
